/**
 * Segmented filter control. Pill style inside card headers; underline style
 * for page-level sections.
 * Spec: assets/handoffs/design_system_export/specs/Tabs.json
 *
 * Roving tabindex: only the selected tab is in the tab order; arrow keys
 * move selection, Home/End jump to first/last.
 *
 * @param {'segmented'|'segmented-compact'|'underline'=segmented} variant - 'segmented-compact' is the tighter preview-toggle track (design handoff README §3)
 * @param {Array<{key:string,label:string,count?:number,disabled?:boolean}>} items - a disabled item is skipped by roving-tabindex arrow/Home/End navigation and ignores clicks
 * @param {string} value - selected item's key
 * @param {function} onChange - (key) => void
 * @param {boolean=false} disabled - disables every tab button (whole control read-only)
 */
import React, { useRef } from "react";
import classNames from "classnames";
import styles from "./Tabs.module.scss";

const Tabs = ({
  variant = "segmented",
  items,
  value,
  onChange,
  disabled = false,
  className,
  ...rest
}) => {
  const refs = useRef([]);

  const focusTab = (index) => refs.current[index]?.focus();

  const select = (index) => {
    if (items[index]?.disabled) return;
    onChange?.(items[index].key);
    focusTab(index);
  };

  const findSelectable = (start, dir) => {
    let index = start;
    for (let i = 0; i < items.length; i++) {
      index = (index + dir + items.length) % items.length;
      if (!items[index]?.disabled) return index;
    }
    return start;
  };

  const firstSelectable = () => items.findIndex((item) => !item?.disabled);
  const lastSelectable = () => {
    for (let i = items.length - 1; i >= 0; i--) {
      if (!items[i]?.disabled) return i;
    }
    return -1;
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      select(findSelectable(index, 1));
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      select(findSelectable(index, -1));
    } else if (e.key === "Home") {
      e.preventDefault();
      const idx = firstSelectable();
      if (idx >= 0) select(idx);
    } else if (e.key === "End") {
      e.preventDefault();
      const idx = lastSelectable();
      if (idx >= 0) select(idx);
    }
  };

  return (
    <div
      role="tablist"
      className={classNames(styles.root, styles[`root--${variant}`], className)}
      {...rest}
    >
      {items.map((item, index) => {
        const selected = item.key === value;
        const hasCount = typeof item.count === "number";
        return (
          <button
            key={item.key}
            ref={(el) => (refs.current[index] = el)}
            type="button"
            role="tab"
            id={`tab-${item.key}`}
            aria-selected={selected}
            aria-controls={`tabpanel-${item.key}`}
            aria-disabled={item.disabled || undefined}
            aria-label={
              hasCount ? `${item.label}, ${item.count} items` : undefined
            }
            tabIndex={selected ? 0 : -1}
            disabled={disabled}
            className={styles.tab}
            onClick={() => select(index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            <span aria-hidden={hasCount || undefined}>{item.label}</span>
            {hasCount && <span className={styles.countPill}>{item.count}</span>}
          </button>
        );
      })}
    </div>
  );
};

export default Tabs;
